// frontend/src/features/medications/hooks/useMedications.js

import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../services/api";
import { adaptMedicationList } from "../adapters/medicationAdapter";

// 🔥 rotina
import useDosesData from "../../routine/hooks/useDosesData";

function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 2,
  }).format(num);
}

export default function useMedications(options = {}) {
  const {
    endpoint = "/flasks",
    enabled = true,
    initialData = [],
  } = options;

  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState("");

  // 🔥 rotina
  const {
    grouped,
    getDoseFlask,
    safeNumber,
    getId, // 🔥 ESSENCIAL
  } = useDosesData();

  // 🔄 FETCH
  const fetchMedications = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(endpoint);
      const responseData = response?.data;

      let items = [];

      if (Array.isArray(responseData)) items = responseData;
      else if (Array.isArray(responseData?.items)) items = responseData.items;
      else if (Array.isArray(responseData?.data)) items = responseData.data;

      setRawItems(items);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Não foi possível carregar suas medicações agora.";

      setError(message);
      setRawItems([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    fetchMedications();
  }, [enabled, fetchMedications]);

  // 🔧 BASE (sem dose)
  const baseMedications = useMemo(() => {
    return adaptMedicationList(rawItems);
  }, [rawItems]);

  // 🔥 MAPA DE DOSE POR ID DE FLASK
  const nextDoseByFlaskId = useMemo(() => {
    const map = new Map();
    const activeDoses = grouped?.active || [];

    activeDoses.forEach((dose) => {
      const flask = getDoseFlask(dose);
      if (!flask) return;

      const flaskId = getId(flask);
      if (!flaskId) return;

      const key = String(flaskId);

      // já tem → ignora (ordem já garante prioridade)
      if (map.has(key)) return;

      const dosageMg = safeNumber(dose?.dosage);

      if (dosageMg != null) {
        map.set(key, dosageMg);
      }
    });

    return map;
  }, [grouped, getDoseFlask, safeNumber, getId]);

  // 🔥 ENRIQUECER MEDICAÇÕES
  const items = useMemo(() => {
    return baseMedications.map((med) => {
      // só injetável usa rotina
      if (med.medicationType !== "INJETAVEL") {
        return med;
      }

      let nextDoseMg = null;

      for (const id of med.packagingItemIds || []) {
        const found = nextDoseByFlaskId.get(String(id));

        if (found !== undefined) {
          nextDoseMg = found;
          break;
        }
      }

      return {
        ...med,
        nextDoseMg,
        secondaryLabel:
          nextDoseMg !== null
            ? `Dose: ${formatNumber(nextDoseMg)} mg`
            : "Dose: —",
      };
    });
  }, [baseMedications, nextDoseByFlaskId]);

  return {
    items,
    loading,
    error,
    refetch: fetchMedications,
  };
}